import JsPDF from 'jspdf';
import moment from 'moment';
import 'jspdf-autotable';
// eslint-disable-next-line import/no-extraneous-dependencies
import { upperFirst, isEmpty, round, sumBy } from 'lodash';

const formatValue = (value) => {
  value = (value || 0);
  value = round(value, 2).toFixed(2);
  return value;
};

const PREP_FEE_LIST = {
  polybagging: 0.46,
  taping: 0.22,
  bundle: 0.46,
  bubblewrapping: 0.80,
  labeling: 0.22
};

const getMappedInvoiceItems = ({ invoice, invoiceItems, chargeList }) => {
  let prepTotal = 0;
  let subTotal = 0;
  const mappedData = invoiceItems.map((invoiceItem) => {
    const {
      title,
      single,
      quantity_in_case: quantityInCase,
      cost: costPrice,
      pack_quantity: packQuantity,
      include_prep_fee: includePrepFee,
      picture_cost
    } = invoiceItem;
    let { upc, vendor_sku: vendorSku, vendor_name: vendorName } = invoiceItem;

    let prepRequired = '';
    let packString = '';
    if (includePrepFee) {
      let prepInstructions = [];
      if (!isEmpty(invoiceItem.prep_instructions)) {
        prepInstructions = invoiceItem.prep_instructions.split(',');
      }

      let prepRequiredTotal = 0;
      prepInstructions.forEach((key) => {
        key = key.replace(/\s+/g, '').toLowerCase();
        if (quantityInCase >= 2 && key === 'polybagging') { }
        else {
          let each = PREP_FEE_LIST[key] || 0;
          let value = ((packQuantity || 0) * each);
          const feeChangeDate = moment("2020-04-20").endOf('day');
          if (key === 'labeling' && moment(invoice.created_at).isAfter(feeChangeDate)) {
            each = 0.34;
            value = ((packQuantity || 0) * each);
          }
          prepTotal += value;
          packString += `${upperFirst(key)}: $${formatValue(value)}\n`;
          prepRequiredTotal += value;

          prepRequired += `${upperFirst(key)}: $${formatValue(each)}\n`;
        }
      });

      if (quantityInCase >= 2) {
        const each = 0.46;
        const packFee = ((packQuantity || 0) * each);
        prepRequiredTotal += packFee;
        packString += `Pack: $${formatValue(packFee)}\n`;
        prepTotal += packFee;

        prepRequired += `Pack: $${formatValue(each)}\n`;
      }

      if (picture_cost) {
        packString += `Picture Cost: $${formatValue(picture_cost)}\n`;
        prepRequiredTotal += picture_cost;
        prepTotal += picture_cost
      }

      packString += `Total: $${formatValue(prepRequiredTotal)}`
    }

    upc = upc || '--';
    vendorSku = vendorSku || '--';
    vendorName = vendorName || '--';

    if (invoiceItem.include_cost === false) {
      return {
        title,
        vendorSku: `${vendorSku}\n${upc}`,
        single: (single || 0),
        packQuantity: (packQuantity || 0),
        quantityInCase: (quantityInCase || 1),
        packString,
        prepRequired,
        vendorName
      };
    }

    const totalPrice = ((costPrice || 0) * (single || 0));
    subTotal += totalPrice;

    return {
      title,
      vendorSku: `${vendorSku}\n${upc}`,
      single: (single || 0),
      packQuantity: (packQuantity || 0),
      quantityInCase: (quantityInCase || 1),
      packString,
      prepRequired,
      costPrice: `$${formatValue(costPrice)}`,
      totalPrice: `$${formatValue(totalPrice)}`,
      vendorName
    };
  });

  chargeList.forEach((charge) => {
    const { charge_type, units, unit_price } = charge;
    const total = (units || 0) * (unit_price || 0);

    if (units > 0) {
      mappedData.push({
        title: charge_type,
        single: units,
        costPrice: `$${formatValue(unit_price)}`,
        totalPrice: `$${formatValue(total)}`
      });

      prepTotal += total;
    }
  })

  return [mappedData, subTotal, prepTotal];
};

const generatePDF = ({
  invoice,
  invoiceItems,
  chargeList,
  companyAddressObj,
  companyName,
  companyLogo
}) => {

  const { paid_date: chargedAt, charged_card_name, inbound_shipment } = invoice;
  const { user, company_address } = inbound_shipment || {};
  const { company_name } = user || {};

  const imageDat = 'data:image/png;base64,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';
  const swiftImage = "data:image/jpeg;base64,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"
  // const defaultAddress = ['7001 Anpesil Dr suite C5', 'North Bergen, NJ 07047, United States', '(347) 586-0271'];
  // const defaultName = 'Daily Distro';
  const defaultAddress = ['4211 35TH ST', 'LONG ISLAND CITY', 'NY, 111012301', '2012557995'];
  const defaultName = 'DAILY DISTRO LLC';
  const defaultSwiftShipAddress = ['1057 Pennsylvania Ave', 'Linden', 'New Jersey 07036', 'United States'];
  const defaultSwiftShipName = 'SWIFT SHIP';

  let [address, name, logo] = [companyAddressObj, companyName, companyLogo];
  let companyDefaultAddress = '';

  if (!logo) {
    if (!name) logo = swiftImage;
    else logo = imageDat;
  }
  if (!name) name = defaultSwiftShipName;
  if (isEmpty(address)) address = defaultSwiftShipAddress;
  else if (address) {
    const { address_line_1, address_line_2, city, state_or_province, zipcode, country } = companyAddressObj || {};
    address = [`${address_line_1 || ''} ${address_line_2 || ''}`, `${city || ''}, ${state_or_province || ''} ${zipcode || ''}, ${country || ''}`];
  }
  if (company_address) {
    const { address_line_1, address_line_2, city, state_or_province, zipcode } = company_address || {};
    companyDefaultAddress = `${address_line_1 || ''} ${address_line_2 || ''}, ${city || ''} ${state_or_province || ''} ${zipcode || ''}`;
  }

  const pdfDoc = new JsPDF('p', 'pt', 'letter');
  const maxY = 765;

  pdfDoc.setDrawColor(0);
  pdfDoc.setFillColor(69, 194, 233);
  pdfDoc.rect(25, 25, 561, 15, 'F');

  pdfDoc.setFillColor(245, 245, 245);
  pdfDoc.rect(25, 40, 561, 100, 'F');


  pdfDoc.addImage(logo, 'PNG', 33, 67, 35, 25);

  pdfDoc.setFont('Times');
  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text(name, 75, 75);
  pdfDoc.text('INVOICE', 450, 75);

  const createdAt = moment(invoice.created_at).format('MMMM DD, YYYY');
  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(address, 75, 90);
  pdfDoc.text([createdAt, invoice.name], 450, 90);

  pdfDoc.line(25, 165, 290, 165);
  pdfDoc.line(320, 165, 585, 165);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('BILL TO', 25, 160);
  pdfDoc.text('SHIP TO', 320, 160);

  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('normal');
  // pdfDoc.text(`${charged_card_name} - ${company_name}`, 25, 185);

  const spitedAddress = companyDefaultAddress?.replaceAll('N/A', '')?.split(',');
  pdfDoc.text(`${user?.full_name?.trim() || ''} - ${company_name || ''}`, 25, 175);
  (companyDefaultAddress && companyDefaultAddress?.length < 60) && pdfDoc.text(`${spitedAddress[0] || ''}, ${spitedAddress[1] || ''}, ${spitedAddress[2] || ''}`, 25, 190);
  if (companyDefaultAddress && companyDefaultAddress?.length > 60) {
    companyDefaultAddress && pdfDoc.text(`${spitedAddress[0] || ''}, ${spitedAddress[1] || ''}, ${spitedAddress[2] || ''}`, 25, 190)
    companyDefaultAddress && pdfDoc.text(`${spitedAddress[3] || ''}`, 25, 200)
  }

  pdfDoc.text(invoice.shipment_id, 320, 175);
  pdfDoc.text(['Amazon', `Amazon ${invoice.fulfillment_center_id}`], 320, 185);
  pdfDoc.text(`${user?.full_name?.trim() || ''} - ${company_name || ''}`, 320, 210);
  (companyDefaultAddress && companyDefaultAddress?.length < 60) && pdfDoc.text(`${spitedAddress[0] || ''}, ${spitedAddress[1] || ''}, ${spitedAddress[2] || ''}`, 320, 222);
  if (companyDefaultAddress && companyDefaultAddress?.length > 60) {
    companyDefaultAddress && pdfDoc.text(`${spitedAddress[0] || ''}, ${spitedAddress[1] || ''}, ${spitedAddress[2] || ''}`, 320, 220)
    companyDefaultAddress && pdfDoc.text(`${spitedAddress[3] || ''}`, 318, 230)
  }

  let [mappedData, subTotal, prepTotal] = getMappedInvoiceItems({
    invoice,
    invoiceItems,
    chargeList
  });

  const columns = [
    { title: 'Description', dataKey: 'title', colSpan: 2 },
    { title: 'Vendor SKU\nUPC', dataKey: 'vendorSku' },
    { title: 'Vendor', dataKey: 'vendorName' },
    { title: 'Prep Required', dataKey: 'prepRequired' },
    { title: 'Bundle Quantity', dataKey: 'quantityInCase' },
    { title: 'Shipped Bundles', dataKey: 'packQuantity' },
    { title: 'Total', dataKey: 'packString' },
    { title: 'Single', dataKey: 'single' },
    { title: 'Price', dataKey: 'costPrice' },
    { title: 'Total', dataKey: 'totalPrice' }
  ];

  const totalDataRows = invoiceItems.length;

  pdfDoc.setFillColor(255, 144, 43);
  pdfDoc.rect(228, 230, 234, 19, 'F');
  pdfDoc.setFillColor(234, 255, 0);
  pdfDoc.rect(462, 230, 133, 19, 'F');

  pdfDoc.setFontSize(9);
  pdfDoc.setFontStyle('bold');
  pdfDoc.setTextColor(255, 255, 255);

  pdfDoc.text('Prep Totals', 233, 242);

  pdfDoc.setTextColor(0, 0, 0);
  pdfDoc.text('Product Costs', 467, 242);

  let tableHeight;
  pdfDoc.autoTable(columns, mappedData, {
    headStyles: {
      fillColor: [69, 194, 233]
    },
    margin: { left: 25, right: 25 },
    columnStyles: {
      title: { cellWidth: 90 },
      vendorSku: { cellWidth: 65 },
      vendorName: { cellWidth: 45 },
      prepRequired: { cellWidth: 70 },
      quantityInCase: { cellWidth: 45 },
      packQuantity: { cellWidth: 45 },
      packString: { cellWidth: 70 },

      single: { cellWidth: 35 },
      costPrice: { cellWidth: 45 },
      totalPrice: { cellWidth: 50 }
    },
    startY: 250,
    styles: {
      overflow: 'linebreak',
      fontSize: 8,
      minCellHeight: 'auto',
      font: 'Times'
    },
    didParseCell(data) {
      if ((data.section === 'body')
        && (data.row.index >= totalDataRows)
        && (data.column.index === 0)) {
        data.cell.colSpan = 2;
      }

      if (data.section === 'body' && ['', undefined, null, NaN].includes(data.cell.raw)) {
        data.cell.styles.halign = 'center';
        data.cell.text = ['--'];
      }
      const { cell, column, section } = data;
      if (section == 'head'
        && (column.dataKey == 'prepRequired'
          || column.dataKey == 'quantityInCase'
          || column.dataKey == 'packQuantity'
          || column.dataKey == 'packString')) {
        cell.styles.fillColor = [255, 144, 43];
      }

      if (section == 'head'
        && (column.dataKey == 'single'
          || column.dataKey == 'costPrice'
          || column.dataKey == 'totalPrice')) {
        cell.styles.fillColor = [234, 255, 0];
        cell.styles.textColor = [0, 0, 0];
      }
    },
    didDrawCell(hookData) {
      const { row } = hookData;
      tableHeight = (row.y + row.height);
    }
  });

  tableHeight += 40;
  pdfDoc.setFontSize(12);

  let subTotalLessDiscount = (subTotal - (invoice.discount || 0));
  let totalTax = ((invoice.tax_rate || 0) * prepTotal * 0.01);
  let totalAmount = (subTotalLessDiscount + prepTotal + totalTax
    + (invoice.shipping_handling_fee || 0));

  let creditCardSurcharge = (totalAmount * 3.5 * 0.01);

  let balanceDue = totalAmount;
  if (!invoice.current_default_source_bank) {
    balanceDue = totalAmount + creditCardSurcharge;
  }

  subTotal = `$${formatValue(subTotal)}`;
  const discount = `$${formatValue(invoice.discount)}`;
  subTotalLessDiscount = `$${formatValue(subTotalLessDiscount)}`;
  const taxRate = `${formatValue(invoice.tax_rate)}%`;
  totalTax = `$${formatValue(totalTax)}`;
  const shippingHandlingFee = `$${formatValue(invoice.shipping_handling_fee)}`;
  prepTotal = `$${formatValue(prepTotal)}`;
  creditCardSurcharge = `$${formatValue(creditCardSurcharge)}`;
  totalAmount = `$${formatValue(totalAmount)}`;

  const aggregateValueList = [
    subTotal,
    discount,
    subTotalLessDiscount,
    prepTotal,
    taxRate,
    totalTax,
    shippingHandlingFee,
    totalAmount
  ];

  const aggregateColumnList = [
    'SUBTOTAL', 'DISCOUNT', 'SUBTOTAL LESS DISCOUNT', 'PREP TOTAL', 'TAX RATE', 'TOTAL TAX', 'SHIPPING/HANDLING', 'TOTAL'
  ];

  if (!invoice.current_default_source_bank) {
    aggregateColumnList.push('CREDIT CARD SURCHARGE (3.5%)');
    aggregateValueList.push(creditCardSurcharge);
  };

  pdfDoc.setFontSize(13);
  pdfDoc.setFontStyle('bold');
  pdfDoc.setTextColor(0, 0, 0);

  if (invoice.current_default_source_bank) {
    pdfDoc.text(`Remarks / Payment Instructions: ACH Withdrawal Acct# ${invoice.account_last_four}`, 25, tableHeight);
  } else {
    pdfDoc.text('Remarks / Payment Instructions: Credit Card', 25, tableHeight);
  }

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  let i = 0;
  while (i < aggregateColumnList.length) {
    const key = aggregateColumnList[i];
    const value = aggregateValueList[i];

    pdfDoc.text(key, 475, tableHeight, 'right');
    pdfDoc.text(value, 495, tableHeight);

    tableHeight += 15;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }

    i += 1;
  }

  pdfDoc.line(400, tableHeight, maxY, tableHeight);

  const totalCalculated = balanceDue;
  const chargedAmount = chargedAt ? balanceDue : 0;
  balanceDue = chargedAt ? 0 : balanceDue;

  const amount = [totalCalculated, balanceDue];
  const heading = ['Total', 'Balance Due'];

  tableHeight = tableHeight + 20;

  let k = 0;
  while (k < amount.length) {
    const key = heading[k];
    const value = amount[k];
    pdfDoc.setFontSize(12);
    pdfDoc.setFontStyle('bold');
    pdfDoc.text(key, 475, tableHeight, 'right');
    pdfDoc.text(`$${formatValue(value)}`, 495, tableHeight);

    tableHeight += 20;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }
    k += 1;
  }

  if (chargedAt) {
    const chargedKeys = ['Total Charged', 'Payment Date'];
    const chargedValues = [`$${formatValue(chargedAmount)}`, moment(chargedAt).format('LLL')]
    let m = 0;
    while (m < chargedValues.length) {
      const key = chargedKeys[m];
      const value = chargedValues[m];
      pdfDoc.setFontSize(12);
      pdfDoc.setFontStyle('bold');
      pdfDoc.text(key, 475, tableHeight, 'right');
      pdfDoc.text(value, 495, tableHeight);

      tableHeight += 20;

      if (tableHeight >= maxY) {
        pdfDoc.addPage();
        tableHeight = 25;
      }
      m += 1;
    }
  }
  pdfDoc.line(400, (tableHeight), maxY, (tableHeight));

  return { pdfDoc: pdfDoc, balanceDue: balanceDue };
};

const generateRemovalInvoicePDF = (invoice) => {
  const {
    name: invoiceName,
    charged_card_name: chargedCardName,
    user_name: userName,
    paid_at: chargedAt,
    createdAt,
    removal_box_invoices: removalBoxInvoices,
    current_default_source_bank,
    account_last_four
  } = invoice;

  const pdfDoc = new JsPDF('p', 'pt', 'letter');
  const maxY = 765;

  pdfDoc.setDrawColor(0);
  pdfDoc.setFillColor(69, 194, 233);
  pdfDoc.rect(25, 25, 561, 15, 'F');

  pdfDoc.setFillColor(245, 245, 245);
  pdfDoc.rect(25, 40, 561, 100, 'F');

  const imageDat = 'data:image/png;base64,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';

  pdfDoc.addImage(imageDat, 'PNG', 33, 67, 35, 25);

  pdfDoc.setFont('Times');
  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('DAILY DISTRO LLC', 75, 75);
  pdfDoc.text('INVOICE', 370, 75);

  const invoiceCreatedAt = moment(createdAt).format('MMMM DD, YYYY');
  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(['4211 35TH ST', 'LONG ISLAND CITY', 'NY, 111012301', '2012557995'], 75, 90);
  pdfDoc.text([invoiceCreatedAt, invoiceName], 370, 90);

  pdfDoc.line(25, 175, 590, 175);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('BILL TO', 25, 170);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(chargedCardName, 25, 185);

  const columns = [
    { title: 'TrackingID', dataKey: 'tracking_number' },
    { title: 'No of Items', dataKey: 'no_of_items' },
    { title: 'Units', dataKey: 'units' },
    { title: 'Box Amount', dataKey: 'box_amount' },
    { title: 'Units Amount', dataKey: 'units_amount' }
  ];

  const arr = removalBoxInvoices.map(({
    tracking_number,
    no_of_items,
    units,
    box_amount,
    units_amount
  }) => ({
    tracking_number,
    no_of_items,
    units,
    box_amount: formatValue(box_amount),
    units_amount: formatValue(units_amount)
  }));

  let totalUnitsAmount = 0;
  let totalBoxAmount = 0;
  removalBoxInvoices.map(({
    units_amount,
    box_amount
  }) => {
    totalUnitsAmount += (units_amount || 0);
    totalBoxAmount += (box_amount || 0);
  });
  const totalDataRows = removalBoxInvoices.length;

  let tableHeight;
  pdfDoc.autoTable(columns, arr, {
    headStyles: { fillColor: [69, 194, 233] },
    margin: { left: 25, right: 25 },
    columnStyles: {
      tracking_number: { cellWidth: 200 },
      no_of_items: { cellWidth: 100 },
      units: { cellWidth: 80 },
      box_amount: { cellWidth: 100 },
      units_amount: { cellWidth: 80 }
    },
    startY: 230,
    styles: {
      overflow: 'linebreak',
      fontSize: 12,
      minCellHeight: 'auto',
      font: 'Times'
    },
    didParseCell(data) {
      if ((data.section === 'body')
        && (data.row.index >= totalDataRows)
        && (data.column.index === 0)) {
        data.cell.colSpan = 2;
      }

      if (data.section === 'body' && ['', undefined, null, NaN].includes(data.cell.raw)) {
        data.cell.styles.halign = 'center';
        data.cell.text = ['--'];
      }
    },
    didDrawCell(hookData) {
      const { row } = hookData;
      tableHeight = (row.y + row.height);
    }
  });

  tableHeight += 20;
  pdfDoc.setFontSize(12);

  let totalAmount = (totalBoxAmount + totalUnitsAmount);
  let creditCardSurcharge = (totalAmount * 3.5 * 0.01);

  let balanceDue = totalAmount;
  if (!current_default_source_bank) {
    balanceDue = totalAmount + creditCardSurcharge;
  }

  totalBoxAmount = formatValue(totalBoxAmount);
  totalUnitsAmount = formatValue(totalUnitsAmount);
  totalAmount = formatValue(totalAmount);
  creditCardSurcharge = formatValue(creditCardSurcharge);

  const aggregateColumnList = [
    'TOTAL BOX AMOUNT', 'TOTAL UNITS AMOUNT', 'TOTAL'
  ];

  const aggregateValueList = [
    totalBoxAmount,
    totalUnitsAmount,
    totalAmount
  ];

  if (!current_default_source_bank) {
    aggregateColumnList.push('CREDIT CARD SURCHARGE (3.5%)');
    aggregateValueList.push(creditCardSurcharge);
  };

  pdfDoc.setFontSize(13);
  pdfDoc.setFontStyle('bold');

  if (invoice.current_default_source_bank) {
    pdfDoc.text(`Remarks / Payment Instructions: ACH Withdrawal Acct# ${account_last_four}`, 25, tableHeight);
  } else {
    pdfDoc.text('Remarks / Payment Instructions: Credit Card', 25, tableHeight);
  }

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  let i = 0;
  while (i < aggregateColumnList.length) {
    const key = aggregateColumnList[i];
    const value = aggregateValueList[i];
    pdfDoc.text(key, 475, tableHeight, 'right');
    pdfDoc.text(value, 495, tableHeight);

    tableHeight += 15;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }

    i += 1;
  }

  pdfDoc.line(400, tableHeight, maxY, tableHeight);

  const totalCalculated = balanceDue;
  const chargedAmount = chargedAt ? balanceDue : 0;
  balanceDue = chargedAt ? 0 : balanceDue;

  const amount = [totalCalculated, balanceDue];
  const heading = ['Total', 'Balance Due'];

  tableHeight = tableHeight + 20;

  let k = 0;
  while (k < amount.length) {
    const key = heading[k];
    const value = amount[k];
    pdfDoc.setFontSize(12);
    pdfDoc.setFontStyle('bold');
    pdfDoc.text(key, 475, tableHeight, 'right');
    pdfDoc.text(`$${formatValue(value)}`, 495, tableHeight);

    tableHeight += 20;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }
    k += 1;
  }

  if (chargedAt) {
    const chargedKeys = ['Total Charged', 'Payment Date'];
    const chargedValues = [`$${formatValue(chargedAmount)}`, moment(chargedAt).format('LLL')]
    let m = 0;
    while (m < chargedValues.length) {
      const key = chargedKeys[m];
      const value = chargedValues[m];
      pdfDoc.setFontSize(12);
      pdfDoc.setFontStyle('bold');
      pdfDoc.text(key, 475, tableHeight, 'right');
      pdfDoc.text(value, 495, tableHeight);

      tableHeight += 20;

      if (tableHeight >= maxY) {
        pdfDoc.addPage();
        tableHeight = 25;
      }
      m += 1;
    }
  }
  pdfDoc.line(400, (tableHeight), maxY, (tableHeight));

  return pdfDoc;
};

const formatAddress = (address) => {
  const {
    title,
    address_line_1,
    address_line_2,
    city,
    state_or_province,
    zipcode,
    country
  } = address || {};

  const addressTextList = [
    title || 'N/A',
    address_line_1 || 'N/A'
  ];

  if (address_line_2) addressTextList.push(address_line_2);
  addressTextList.push(`${city || 'N/A'} ${state_or_province || 'N/A'} ${zipcode || 'N/A'}, ${country || 'N/A'}`);

  return addressTextList;
}

const generatePurchaseOrderPDF = ({
  orderInformation,
  orderLineItems
}) => {
  const {
    name,
    vendor,
    date,
    shipFrom,
    shipTo,
    imageUrl,
    companyName,
  } = orderInformation;

  let {
    customer_code,
    po_code
  } = orderInformation;

  customer_code = customer_code || 'N/A';
  po_code = po_code || name || 'N/A';

  const pdfDoc = new JsPDF('p', 'pt', 'letter');
  const maxY = 750;

  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('bold');
  pdfDoc.setTextColor(255, 0, 0);
  pdfDoc.text([`PLEASE REFERENCE CUSTOMER PO ON INVOICE AND PACKING SLIP : ${po_code}`], 50, 40);

  pdfDoc.setTextColor(0, 0, 0);
  pdfDoc.setFillColor(245, 245, 245);
  pdfDoc.rect(25, 50, 561, 90, 'F');

  if (imageUrl) {
    pdfDoc.addImage(imageUrl, 'PNG', 33, 67, 35, 25);
  } else {
    pdfDoc.setFontSize(30);
    pdfDoc.setFontStyle('bold');
    pdfDoc.text(companyName[0], 40, 90);
  }

  pdfDoc.setFont('Times');
  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text(companyName, 75, 80);
  pdfDoc.text('PURCHASE ORDER', 450, 75);

  pdfDoc.setFontSize(9);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(`CUSTOMER CODE: ${customer_code}`, 75, 90);
  pdfDoc.text(`PO #: ${po_code}`, 75, 100);

  const createdAt = date;
  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text([createdAt], 450, 90);

  pdfDoc.line(25, 175, 280, 175);
  pdfDoc.line(290, 175, 585, 175);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('SHIP FROM', 25, 170);
  pdfDoc.text('SHIP TO', 290, 170);

  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('normal');

  const shipFromList = formatAddress(shipFrom);

  let y = 185;
  shipFromList.forEach((line) => {
    pdfDoc.text(line, 25, y, { maxWidth: 200 });
    y = y + 12;
  });

  const shipToList = formatAddress(shipTo);

  y = 185;
  let t = 0;
  shipToList.forEach((line) => {
    if (t == 1) {
      pdfDoc.setTextColor(255, 0, 0);
      pdfDoc.text(`PO #: ${po_code}`, 290, y, { maxWidth: 200 });
      pdfDoc.setTextColor(0, 0, 0);
      y = y + 12;
    }
    pdfDoc.text(line, 290, y, { maxWidth: 200 });
    y = y + 12;
    t += 1;
  });

  const columns = [
    { title: 'Product Name', dataKey: 'productName' },
    { title: 'Vendor SKU', dataKey: 'vendorsku' },
    { title: 'UPC', dataKey: 'itemUpc' },
    { title: 'Total Units Buy', dataKey: 'totalUnitBuy' },
    { title: 'Total Case Buy', dataKey: 'totalCaseBuy' },
    { title: 'Unit Cost', dataKey: 'unitsCost' },
    { title: 'Subtotal', dataKey: 'subTotal' }
  ];

  const mappedPoList = orderLineItems.map(({ vendoritem, order_quantity, id, case_quantity, cost }) => {
    const {
      vendortitle,
      upc,
      vendorsku
    } = vendoritem || {};

    cost = cost || vendoritem.cost;

    return {
      totalUnitBuy: order_quantity,
      itemNo: id,
      productName: vendortitle,
      vendorsku,
      unitsCost: formatValue(Number(cost)),
      subTotal: formatValue((order_quantity * Number(cost))),
      caseUpc: '',
      totalCaseBuy: (order_quantity / (case_quantity || 1)),
      itemUpc: upc
    };
  });

  const totalDataRows = orderLineItems.length;

  let tableHeight;
  pdfDoc.autoTable(columns, mappedPoList, {
    headStyles: { fillColor: [255, 255, 255], textColor: 'black' },
    margin: { left: 25, right: 25 },
    columnStyles: {
      totalUnitBuy: { cellWidth: 80 },
      itemNo: { cellWidth: 50 },
      productName: { cellWidth: 120 },
      vendorsku: { cellWidth: 60 },
      unitsCost: { cellWidth: 70 },
      subTotal: { cellWidth: 70 },
      caseUpc: { cellWidth: 60 },
      totalCaseBuy: { cellWidth: 70 },
      itemUpc: { cellWidth: 90 }
    },
    startY: 250,
    styles: {
      overflow: 'linebreak',
      fontSize: 12,
      minCellHeight: 'auto',
      font: 'Times'
    },
    didParseCell(data) {
      if ((data.section === 'body')
        && (data.row.index >= totalDataRows)
        && (data.column.index === 0)) {
        data.cell.colSpan = 2;
      }

      if (data.section === 'body' && ['', undefined, null, NaN].includes(data.cell.raw)) {
        data.cell.styles.halign = 'center';
        data.cell.text = ['--'];
      }
    },
    didDrawCell(hookData) {
      const { row } = hookData;
      tableHeight = (row.y + row.height);
    }
  });

  tableHeight += 20;
  pdfDoc.setFontSize(12);

  const aggregateColumnList = [
    'SUBTOTAL', 'GRAND TOTAL'
  ];

  let subTotalValue = 0;
  let grandTotal = 0;
  orderLineItems.forEach(({ vendoritem, order_quantity }) => {
    const { cost } = vendoritem || {};
    const subTotal = (Number(cost || 0) * order_quantity);
    subTotalValue += subTotal;
  });
  grandTotal = subTotalValue;

  const aggregateValueList = [
    subTotalValue,
    grandTotal
  ];

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  let i = 0;
  while (i < aggregateColumnList.length) {
    const key = aggregateColumnList[i];
    const value = aggregateValueList[i];
    pdfDoc.text(key, 510, tableHeight, 'right');
    pdfDoc.text(formatValue(value), 535, tableHeight);

    tableHeight += 15;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }

    i += 1;
  }

  pdfDoc.line(400, tableHeight, maxY, tableHeight);

  pdfDoc.setFontSize(15);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('TOTAL', 500, tableHeight + 20, 'right');
  pdfDoc.text(formatValue(grandTotal), 525, tableHeight + 20);

  pdfDoc.line(400, (tableHeight + 30), maxY, (tableHeight + 30));

  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('bold');
  pdfDoc.line(25, 730, 550, 730);
  pdfDoc.text(['PLEASE PRINT AND PLACE A COPY OF THIS PO WITH THE PACKING SLIP'], 50, 750);

  return pdfDoc;
};

export const downloadCustomerInvoice = ({ invoice, invoiceItems, chargeList, companyAddressObj, companyName, companyLogo }) => {
  const { pdfDoc } = generatePDF({ invoice, invoiceItems, chargeList, companyAddressObj, companyName, companyLogo });
  pdfDoc.save(`${invoice.name || 'INVOICE'}.pdf`);
};

export const downloadPurchaseOrderPDF = ({ orderInformation, orderLineItems }) => {
  const pdfDoc = generatePurchaseOrderPDF({ orderInformation, orderLineItems });
  pdfDoc.save(`${orderInformation.name || 'PURCHASE ORDER'}.pdf`);
};

export const downloadRemovalInvoice = (invoice) => {
  const pdfDoc = generateRemovalInvoicePDF(invoice);
  pdfDoc.save(`${invoice.name || 'INVOICE'}.pdf`);
}

export const downloadTwoStepInvoice = (invoice) => {
  const pdfDoc = generateTwoStepInvoicePDF(invoice);
  pdfDoc.save(`${invoice?.user?.loginEmail || 'TWO-STEP-INVOICE'}.pdf`);
}

export const getPOPDF = ({ orderInformation, orderLineItems }) => {
  let pdfDoc = generatePurchaseOrderPDF({ orderInformation, orderLineItems });
  pdfDoc = pdfDoc.output('datauristring');
  return pdfDoc;
}


export const downloadStorageInvoice = (invoice) => {
  const pdfDoc = generateStorageInvoicePDF(invoice);
  pdfDoc.save(`${invoice.name || 'INVOICE'}.pdf`);
};

export const calculateTotalAmount = ({ invoice, invoiceItems, chargeList }) => {
  const [mappedData, subTotal, prepTotal] = getMappedInvoiceItems({
    invoice,
    invoiceItems,
    chargeList
  });

  const subTotalLessDiscount = (subTotal - (invoice.discount || 0));
  const totalTax = ((invoice.tax_rate || 0) * prepTotal * 0.01);
  const totalAmount = (subTotalLessDiscount + prepTotal + totalTax
    + (invoice.shipping_handling_fee || 0));

  let balanceDue = totalAmount;
  if (!invoice.current_default_source_bank) {
    const creditCardSurcharge = (totalAmount * 3.5 * 0.01);
    balanceDue = totalAmount + creditCardSurcharge;
  }

  return formatValue(balanceDue);
};

export const getCustomerInvoicePDF = ({ invoice, invoiceItems, chargeList, companyAddressObj, companyName, companyLogo }) => {
  const { pdfDoc, balanceDue } = generatePDF({ invoice, invoiceItems, chargeList, companyAddressObj, companyName, companyLogo });
  const previewPDF = pdfDoc.output('datauristring');
  return [pdfDoc, previewPDF, balanceDue];
};

export const getRemovalInvoicePDF = (invoice) => {
  const pdfDoc = generateRemovalInvoicePDF(invoice);
  const previewPDF = pdfDoc.output('datauristring');
  return [pdfDoc, previewPDF];
};

const generateStorageInvoicePDF = (invoice) => {
  const {
    name: invoiceName,
    current_default_source_bank,
    account_last_four,
    paid_at: chargedAt,
    locations,
    charged_card_name: chargedCardName,
    user_name: userName,
    createdAt,
    items
  } = invoice;

  const pdfDoc = new JsPDF('p', 'pt', 'letter');
  const maxY = 770;

  pdfDoc.setDrawColor(0);
  pdfDoc.setFillColor(69, 194, 233);
  pdfDoc.rect(25, 25, 561, 15, 'F');

  pdfDoc.setFillColor(245, 245, 245);
  pdfDoc.rect(25, 40, 561, 100, 'F');

  const imageDat = 'data:image/png;base64,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';

  pdfDoc.addImage(imageDat, 'PNG', 33, 67, 35, 25);

  pdfDoc.setFont('Times');
  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('DAILY DISTRO LLC', 75, 75);
  pdfDoc.text('INVOICE', 370, 75);

  const invoiceCreatedAt = moment(createdAt).format('MMMM DD, YYYY');
  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(['4211 35TH ST', 'LONG ISLAND CITY', 'NY, 111012301', '2012557995'], 75, 90);
  pdfDoc.text(invoiceCreatedAt, 370, 90);
  pdfDoc.text(invoiceName || 'INVOICE', 370, 103);

  pdfDoc.line(25, 175, 590, 175);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('BILL TO', 25, 170);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(chargedCardName, 25, 185);

  const columns = [
    { title: 'Item Name', dataKey: 'itemName' },
    { title: 'Asin', dataKey: 'itemAsin' },
    { title: 'Location', dataKey: 'itemLocation' },
    { title: 'Quantity', dataKey: 'itemQuantity' },
    { title: 'Cost Per unit', dataKey: 'costPerUnit' },
    { title: 'Total Cost', dataKey: 'costTotal' }
  ];

  const itemList = []
  items?.forEach(({ item_name, asin, location, quantity, per_unit_amount, total_amount }) => {
    itemList.push({
      itemName: item_name,
      itemAsin: asin,
      itemLocation: location,
      itemQuantity: quantity,
      costPerUnit: round(per_unit_amount, 4),
      costTotal: round(total_amount, 2)
    })
  })

  let totalAmount = sumBy(items, 'total_amount') || 0;
  let creditCardSurcharge = (totalAmount * 3.5 * 0.01);

  let balanceDue = totalAmount;
  if (!current_default_source_bank) {
    balanceDue = totalAmount + creditCardSurcharge;
  }

  totalAmount = formatValue(totalAmount);
  creditCardSurcharge = formatValue(creditCardSurcharge);

  let tableHeight;
  pdfDoc.autoTable(columns, itemList, {
    headStyles: { fillColor: [69, 194, 233] },
    margin: { left: 25, right: 25 },
    columnStyles: {
      itemName: { cellWidth: 100 },
      itemAsin: { cellWidth: 100 },
      itemDimensions: { cellWidth: 80 },
      itemLocation: { cellWidth: 100 },
      itemQuantity: { cellWidth: 60 },
      costPerUnit: { cellWidth: 50 },
      costTotal: { cellWidth: 50 }
    },
    startY: 230,
    styles: {
      overflow: 'linebreak',
      fontSize: 12,
      minCellHeight: 'auto',
      font: 'Times'
    },
    didDrawCell(hookData) {
      const { row } = hookData;
      tableHeight = (row.y + row.height);
    }
  });

  tableHeight += 20;
  pdfDoc.setFontSize(12);

  const aggregateColumnList = [
    'TOTAL COST'
  ];

  const aggregateValueList = [
    totalAmount
  ];

  if (!current_default_source_bank) {
    aggregateColumnList.push('CREDIT CARD SURCHARGE (3.5%)');
    aggregateValueList.push(creditCardSurcharge);
  };

  pdfDoc.setFontSize(13);
  pdfDoc.setFontStyle('bold');

  if (current_default_source_bank) {
    pdfDoc.text(`Remarks / Payment Instructions: ACH Withdrawal Acct# ${account_last_four}`, 25, tableHeight);
  } else {
    pdfDoc.text('Remarks / Payment Instructions: Credit Card', 25, tableHeight);
  }

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  let i = 0;
  while (i < aggregateColumnList.length) {
    const key = aggregateColumnList[i];
    const value = aggregateValueList[i];
    pdfDoc.text(key, 475, tableHeight, 'right');
    pdfDoc.text(value, 495, tableHeight);

    tableHeight += 15;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }

    i += 1;
  }

  pdfDoc.line(400, tableHeight, maxY, tableHeight);

  const totalCalculated = balanceDue;
  const chargedAmount = chargedAt ? balanceDue : 0;
  balanceDue = chargedAt ? 0 : balanceDue;

  const amount = [totalCalculated, balanceDue];
  const heading = ['Total', 'Balance Due'];

  tableHeight = tableHeight + 20;

  let k = 0;
  while (k < amount.length) {
    const key = heading[k];
    const value = amount[k];
    pdfDoc.setFontSize(12);
    pdfDoc.setFontStyle('bold');
    pdfDoc.text(key, 475, tableHeight, 'right');
    pdfDoc.text(`$${formatValue(value)}`, 495, tableHeight);

    tableHeight += 20;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }
    k += 1;
  }

  if (chargedAt) {
    const chargedKeys = ['Total Charged', 'Payment Date'];
    const chargedValues = [`$${formatValue(chargedAmount)}`, moment(chargedAt).format('LLL')]
    let m = 0;
    while (m < chargedValues.length) {
      const key = chargedKeys[m];
      const value = chargedValues[m];
      pdfDoc.setFontSize(12);
      pdfDoc.setFontStyle('bold');
      pdfDoc.text(key, 475, tableHeight, 'right');
      pdfDoc.text(value, 495, tableHeight);

      tableHeight += 20;

      if (tableHeight >= maxY) {
        pdfDoc.addPage();
        tableHeight = 25;
      }
      m += 1;
    }
  }
  pdfDoc.line(400, (tableHeight), maxY, (tableHeight));

  return pdfDoc;
};

export const getTwoStepInvoicePDF = (invoice) => {
  const pdfDoc = generateTwoStepInvoicePDF(invoice);
  const previewPDF = pdfDoc.output('datauristring');
  return [pdfDoc, previewPDF];
};

const generateTwoStepInvoicePDF = (invoice) => {
  let {
    user: { loginEmail: invoiceEmail, name: userName },
    createdAt,
    shippingFee: totalShippingFee,
    packagingFee: totalPackingFee,
    totalAmount: totalInvoiceAmount,
    invoiceItems: twoStepInvoices,
    paymentMethod,
    paidDate
  } = invoice;

  const pdfDoc = new JsPDF('p', 'pt', 'letter');
  const maxY = 765;

  pdfDoc.setDrawColor(0);
  pdfDoc.setFillColor(69, 194, 233);
  pdfDoc.rect(25, 25, 561, 15, 'F');

  pdfDoc.setFillColor(245, 245, 245);
  pdfDoc.rect(25, 40, 561, 100, 'F');

  const imageDat = 'data:image/png;base64,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';

  pdfDoc.addImage(imageDat, 'PNG', 33, 67, 35, 25);

  pdfDoc.setFont('Times');
  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('DAILY DISTRO LLC', 75, 75);
  pdfDoc.text('TWO STEP INVOICE', 370, 75);

  const invoiceCreatedAt = moment(createdAt).format('MMMM DD, YYYY');
  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(['4211 35TH ST', 'LONG ISLAND CITY', 'NY, 111012301', '2012557995'], 75, 90);
  pdfDoc.text([invoiceCreatedAt, invoiceEmail], 370, 90);

  pdfDoc.line(25, 175, 590, 175);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('BILL TO', 25, 170);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(userName, 25, 185);
  pdfDoc.text(invoiceEmail, 25, 200);

  const columns = [
    { title: 'No.', dataKey: 'count' },
    { title: 'Purchase Order Id', dataKey: 'purchaseOrderId' },
    { title: 'No of Items', dataKey: 'itemCount' },
    { title: 'Packing Fee', dataKey: 'packagingFee' },
    { title: 'Shipping Fee', dataKey: 'shippingFee' },
    { title: 'Total', dataKey: 'totalAmount' }
  ];

  let count = 1;
  const arr = twoStepInvoices.map(({
    itemCount,
    order,
    packagingFee,
    shippingFee,
    totalAmount
  }) => ({
    count: count++,
    itemCount,
    purchaseOrderId: order.marketplaceOrderId,
    packagingFee: `$${formatValue(packagingFee)}`,
    shippingFee: `$${formatValue(shippingFee)}`,
    totalAmount: `$${formatValue(totalAmount)}`
  }));

  let totalUnitsAmount = 0;
  let totalBoxAmount = 0;
  // removalBoxInvoices.map(({
  //   units_amount,
  //   box_amount
  // }) => {
  //   totalUnitsAmount += (units_amount || 0);
  //   totalBoxAmount += (box_amount || 0);
  // });
  const totalDataRows = twoStepInvoices.length;

  let tableHeight;
  pdfDoc.autoTable(columns, arr, {
    headStyles: { fillColor: [69, 194, 233] },
    margin: { left: 25, right: 25 },
    columnStyles: {
      count: { cellWidth: 70 },
      purchaseOrderId: { cellWidth: 130 },
      itemCount: { cellWidth: 100 },
      packagingFee: { cellWidth: 90 },
      shippingFee: { cellWidth: 90 },
      totalAmount: { cellWidth: 80 }
    },
    startY: 230,
    styles: {
      overflow: 'linebreak',
      fontSize: 12,
      minCellHeight: 'auto',
      font: 'Times'
    },
    didParseCell(data) {
      if ((data.section === 'body')
        && (data.row.index >= totalDataRows)
        && (data.column.index === 0)) {
        data.cell.colSpan = 2;
      }

      if (data.section === 'body' && ['', undefined, null, NaN].includes(data.cell.raw)) {
        data.cell.styles.halign = 'center';
        data.cell.text = ['--'];
      }
    },
    didDrawCell(hookData) {
      const { row } = hookData;
      tableHeight = (row.y + row.height);
    }
  });

  tableHeight += 20;
  pdfDoc.setFontSize(12);

  let totalAmount = totalInvoiceAmount;
  let creditCardSurcharge = (totalAmount * 3.5 * 0.01);
  let balanceDue = totalAmount;
  if (paymentMethod === "Card" && moment(paidDate) < moment('2022-04-27 11:07:18.475+05')) {
    balanceDue = totalAmount + creditCardSurcharge;
  }

  totalShippingFee = formatValue(totalShippingFee);
  totalPackingFee = formatValue(totalPackingFee);
  totalAmount = formatValue(totalAmount);
  creditCardSurcharge = formatValue(creditCardSurcharge);

  const aggregateColumnList = [
    'TOTAL PACKING AMOUNT', 'TOTAL SHIPPING AMOUNT', 'TOTAL'
  ];

  const aggregateValueList = [
    totalPackingFee,
    totalShippingFee,
    totalAmount
  ];

  if (paymentMethod === "Card" && moment(paidDate) < moment('2022-04-27 11:07:18.475+05')) {
    aggregateColumnList.push('CREDIT CARD SURCHARGE (3.5%)');
    aggregateValueList.push(creditCardSurcharge);
  };

  pdfDoc.setFontSize(13);
  pdfDoc.setFontStyle('bold');

  if (invoice.paymentMethod === "Card" && moment(paidDate) < moment('2022-04-27 11:07:18.475+05')) {
    pdfDoc.text('Remarks / Payment Instructions: Credit Card', 25, tableHeight);
  }

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  let i = 0;
  while (i < aggregateColumnList.length) {
    const key = aggregateColumnList[i];
    const value = aggregateValueList[i];
    pdfDoc.text(key, 510, tableHeight, 'right');
    pdfDoc.text(`$${value}`, 535, tableHeight);

    tableHeight += 15;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }

    i += 1;
  }

  pdfDoc.line(400, tableHeight, maxY, tableHeight);

  pdfDoc.setFontSize(15);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('BALANCE DUE', 500, tableHeight + 20, 'right');
  pdfDoc.text(`$${formatValue(balanceDue)}`, 525, tableHeight + 20);

  pdfDoc.line(400, (tableHeight + 30), maxY, (tableHeight + 30));

  return pdfDoc;
};

export const getStorageInvoicePDF = (invoice, storage_invoice_pdf_data) => {
  const pdfDoc = generateStorageInvoicePDF(invoice, storage_invoice_pdf_data);
  const previewPDF = pdfDoc.output('datauristring');
  return [pdfDoc, previewPDF];
};

const generateSubscriptionInvoicePDF = ({
  invoiceName,
  userName,
  status,
  chargedAt,
  chargedCardName,
  createdAt,
  description,
  amountPaid,
  amountDue,
  invoiceFrom,
  invoiceTo
}) => {
  const pdfDoc = new JsPDF('p', 'pt', 'letter');
  const maxY = 765;

  pdfDoc.setDrawColor(0);
  pdfDoc.setFillColor(69, 194, 233);
  pdfDoc.rect(25, 25, 561, 15, 'F');

  pdfDoc.setFillColor(245, 245, 245);
  pdfDoc.rect(25, 40, 561, 100, 'F');

  const imageDat = 'data:image/png;base64,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';

  pdfDoc.addImage(imageDat, 'PNG', 33, 67, 35, 25);

  pdfDoc.setFont('Times');
  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('DAILY DISTRO LLC', 75, 75);
  pdfDoc.text('SUBSCRIPTION INVOICE', 370, 75);

  const invoiceCreatedAt = moment(createdAt).format('MMMM DD, YYYY');
  const from = `From:  ${moment(invoiceFrom).format('MMMM DD, YYYY')}`;
  const to = `To:       ${moment(invoiceTo).format('MMMM DD, YYYY')}`;

  pdfDoc.setFontSize(10);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(['4211 35TH ST', 'LONG ISLAND CITY', 'NY, 111012301', '2012557995'], 75, 90);
  pdfDoc.text([invoiceCreatedAt, from, to], 370, 90);

  pdfDoc.line(25, 175, 590, 175);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('BILL TO', 25, 170);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  pdfDoc.text(chargedCardName || '', 25, 185);

  const columns = [
    { title: 'Description', dataKey: 'description' },
    { title: 'Amount', dataKey: 'amountPaid' }
  ];

  const arr = [{
    description,
    amountPaid: `$${amountPaid}`
  }];

  const totalDataRows = arr.length;

  let tableHeight;
  pdfDoc.autoTable(columns, arr, {
    headStyles: {
      fillColor: [220, 220, 220],
      textColor: [0, 0, 0]
    },
    margin: { left: 25, right: 25 },
    columnStyles: {
      discription: { cellWidth: 250 },
      amountPaid: { cellWidth: 60, halign: 'left' }
    },
    startY: 230,
    styles: {
      overflow: 'linebreak',
      fontSize: 12,
      minCellHeight: 'auto',
      font: 'Times'
    },
    didParseCell(data) {
      if ((data.section === 'body')
        && (data.row.index >= totalDataRows)
        && (data.column.index === 0)) {
        data.cell.colSpan = 2;
      }

      if (data.section === 'body' && ['', undefined, null, NaN].includes(data.cell.raw)) {
        data.cell.styles.halign = 'center';
        data.cell.text = ['--'];
      }
    },
    didDrawCell(hookData) {
      const { row } = hookData;
      tableHeight = (row.y + row.height);
    }
  });

  tableHeight += 30;
  pdfDoc.setFontSize(12);

  const aggregateColumnList = [
    'Total Due', 'Total Paid'
  ];
  const aggregateValueList = [
    amountDue, amountPaid
  ];

  pdfDoc.setFontSize(13);
  pdfDoc.setFontStyle('bold');
  pdfDoc.text('Remarks / Payment Instructions: Credit Card / Bank Account', 25, tableHeight);

  pdfDoc.setFontSize(12);
  pdfDoc.setFontStyle('normal');
  let i = 0;
  while (i < aggregateColumnList.length) {
    const key = aggregateColumnList[i];
    const value = aggregateValueList[i];
    pdfDoc.text(key, 450, tableHeight, 'right');
    pdfDoc.text(`$${value}`, 470, tableHeight);

    tableHeight += 15;

    if (tableHeight >= maxY) {
      pdfDoc.addPage();
      tableHeight = 25;
    }

    i += 1;
  }

  if (chargedAt || (invoiceFrom && amountPaid)) {
    pdfDoc.text('Payment Date', 470, tableHeight, 'right');
    pdfDoc.text(moment(chargedAt || invoiceFrom).format('LLL'), 480, tableHeight);
  }

  pdfDoc.line(400, tableHeight + 10, maxY, tableHeight + 10);

  pdfDoc.setFontSize(15);

  return pdfDoc;
};

export const downloadSubscriptionInvoicePDF = ({
  invoiceName,
  chargedCardName,
  userName,
  createdAt,
  description,
  amountPaid,
  amountDue,
  invoiceFrom,
  invoiceTo,
  chargedAt,
  status,
}) => {
  const pdfDoc = generateSubscriptionInvoicePDF({
    invoiceName,
    userName,
    chargedCardName,
    createdAt,
    description,
    amountPaid,
    amountDue,
    invoiceFrom,
    invoiceTo,
    status,
    chargedAt,
  });
  pdfDoc.save(`${invoiceName || 'INVOICE'}.pdf`);
};
